import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { listIssuedShares, revokeShare } from '../utils/apiContentSharing';
import { getApiUrl } from '../config';
import { getAccessToken, logout } from '../utils/auth';
import {
  asArray,
  shareTitle,
  shareChapterLabel,
  shareRecipient,
  formatShareDate,
  statusBadgeClass,
  statusLabel,
} from '../utils/sharesFields';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending_acceptance', label: 'Pending' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'expired', label: 'Expired' },
];

function IssuedSharesList() {
  const [shares, setShares] = useState([]);
  const [titlesByFileId, setTitlesByFileId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState('');

  // /shares/issued does not include book titles, so we look them up against
  // the owner's /files list. Failure to load files is non-fatal — the table
  // still renders with file ids in place of titles.
  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return undefined;
    axios
      .get(getApiUrl('projects'), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const p of res.data?.projects || []) {
          if (p && p.id) map[p.id] = p.title || null;
        }
        setTitlesByFileId(map);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listIssuedShares(null, statusFilter || null);
      setShares(asArray(data));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError(err.request ? 'Network error: Could not load shares.' : 'Could not load shares.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (id) => {
    setRevokingId(id);
    setRevokeError('');
    try {
      await revokeShare(id);
      setShares((prev) => prev.filter((s) => s.id !== id));
      setConfirmingId(null);
    } catch (err) {
      setRevokeError('Could not revoke this share. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="ml-auto px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {revokeError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{revokeError}</p>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading shares...</p>}
      {!loading && error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
      {!loading && !error && shares.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No shares match the current filters.</p>
      )}
      {!loading && !error && shares.length > 0 && (
        <SharesTable
          shares={shares}
          titlesByFileId={titlesByFileId}
          confirmingId={confirmingId}
          revokingId={revokingId}
          onAskRevoke={setConfirmingId}
          onCancelRevoke={() => setConfirmingId(null)}
          onConfirmRevoke={handleRevoke}
        />
      )}
    </div>
  );
}

export default IssuedSharesList;

function SharesTable({ shares, titlesByFileId, confirmingId, revokingId, onAskRevoke, onCancelRevoke, onConfirmRevoke }) {
  return (
    <div className="overflow-x-auto bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <Th>Recipient</Th>
            <Th>Book</Th>
            <Th className="hidden md:table-cell">Status</Th>
            <Th className="hidden lg:table-cell">Created</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {shares.map((s) => (
            <ShareRow
              key={s.id}
              share={s}
              titlesByFileId={titlesByFileId}
              isConfirming={confirmingId === s.id}
              isRevoking={revokingId === s.id}
              onAskRevoke={() => onAskRevoke(s.id)}
              onCancelRevoke={onCancelRevoke}
              onConfirmRevoke={() => onConfirmRevoke(s.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function ShareRow({ share, titlesByFileId, isConfirming, isRevoking, onAskRevoke, onCancelRevoke, onConfirmRevoke }) {
  const lookedUpTitle = titlesByFileId?.[share.file_id] || null;
  const title = lookedUpTitle || shareTitle(share);
  const isHighlight = !!share.highlight_chapter_id;
  const chapterLabel = shareChapterLabel(share);
  const canRevoke = !['revoked', 'expired'].includes((share.status || '').toLowerCase());
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
      <td className="px-4 py-3 text-gray-900 dark:text-white">{shareRecipient(share)}</td>
      <td className="px-4 py-3 text-gray-900 dark:text-white">
        <div className="flex flex-wrap items-center gap-2">
          <span>{title}</span>
          {isHighlight && (
            <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
              Chapter highlight
            </span>
          )}
        </div>
        {isHighlight && chapterLabel && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{chapterLabel}</div>
        )}
      </td>
      <td className="hidden md:table-cell px-4 py-3">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClass(share.status)}`}>
          {statusLabel(share.status)}
        </span>
      </td>
      <td className="hidden lg:table-cell px-4 py-3 text-gray-500 dark:text-gray-400">
        {formatShareDate(share.created_at)}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {canRevoke && !isConfirming && (
          <button
            type="button"
            onClick={onAskRevoke}
            disabled={isRevoking}
            className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Revoke
          </button>
        )}
        {isConfirming && (
          <span className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={onConfirmRevoke}
              disabled={isRevoking}
              className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              {isRevoking ? 'Revoking...' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={onCancelRevoke}
              disabled={isRevoking}
              className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}
